/**
 * Fixture schema — mirrors OPBridge's shape (DEC-104 / DEC-105 / DEC-267).
 *
 * Ownership: OPBridge owns the canonical fixture library (rig truth per the
 * three-way data split; user-scoped, peer-synced, lives in
 * `%APPDATA%/OverpoweredApps/OPBridge/fixtures.json`). The PWA holds a
 * local cache from last connect + a temporary local seed for dev/prototype
 * use. When connected to OPBridge, the PWA reads `/fixtures` for the live
 * list; when disconnected, the cache is authoritative for the UI.
 *
 * Capability vocabulary (DEC-267, 12 names): intensity / color.r /
 * color.g / color.b / colorPreset / strobe / pan / tilt / gobo / effect /
 * speed / mode. Second effect etc. escape via `raw.<name>`. Adding names
 * is additive; renaming requires a protocol bump.
 *
 * Profile registry (fixture make/model definitions) is build-time vendored
 * in OPBridge's `opbridge-fixtures` crate, NOT peer-synced (machine truth
 * per DEC-105).
 *
 * `[UPDATED 2026-09-17]` - this comment used to say the PWA "doesn't hold
 * profiles today" and would switch "when OPBridge ships a
 * `GET /fixtures/profiles` endpoint". That endpoint has existed since
 * OPBridge Session 141, at `GET /dmx/profiles`, and the switch never
 * happened: the PWA kept resolving from a hardcoded one-entry table.
 * Measured against the operator's real five-fixture rig, three fixtures
 * resolved ZERO capabilities and one rendered its raw `profileId` as its
 * label. The bridge catalogue is now the primary source
 * (`fixtures/profileSync.ts`); the inline `cachedProfile` and the built-in
 * seed are the offline fallbacks behind it.
 */

/** OPBridge's 12-name capability vocabulary (DEC-267). `raw.<name>` allowed
 *  as an escape hatch. */
export type FixtureCapabilityName =
  | 'intensity'
  | 'color.r'
  | 'color.g'
  | 'color.b'
  | 'colorPreset'
  | 'strobe'
  | 'pan'
  | 'tilt'
  | 'gobo'
  | 'effect'
  | 'speed'
  | 'mode'
  | (string & { readonly __rawEscape?: unique symbol });

/** Capability channel domain — how normalized [0..1] values map to bytes.
 *  Linear = value * 255 (default). Preset = discrete step ranges (color
 *  wheel presets etc.); details deferred to when a preset fixture ships. */
export type CapabilityDomain =
  | { kind: 'linear' }
  | { kind: 'gamma'; gamma: number }
  | { kind: 'preset'; steps: Array<{ value: number; label: string }> };

/** One capability on a fixture profile mode.
 *
 *  `channelIndex` is a byte offset into the fixture's DMX footprint **only
 *  when the profile came from the built-in seed**, which carries real offsets
 *  from OPBridge's vendored JSON. A profile resolved from `GET /dmx/profiles`
 *  sets it to `-1`, because that endpoint does not publish offsets: its
 *  `capabilities` array is a SET of what a mode can do, not a positional map.
 *  Inferring offsets from it is a known, measured mistake - it cost OPStage a
 *  session and rendered blue as red - and it is a nasty one because position
 *  happens to hold for a 3-channel SlimPAR and for the COLORband's blocks
 *  while failing on the PL60C. See `fixtures/profileSync.ts::fromWireMode`. */
export interface FixtureCapability {
  capability: FixtureCapabilityName;
  channelIndex: number;
  domain: CapabilityDomain;
  /** Optional human label — the editor falls back to the capability name
   *  when absent. */
  label?: string;
}

/** One addressable zone within a multi-zone fixture (OPBridge calls these
 *  sub-fixtures; the operator calls them Blocks). A COLORband PiX in
 *  12-channel mode has four. Empty for single-block fixtures.
 *
 *  `index` is what `POST /dmx/fixtures/{id}/color|state|effect` takes as
 *  `subfixture`. Omitting it addresses the whole fixture - which for a
 *  multi-zone fixture fans the write to every zone, not just the first.
 *  Added 2026-09-17: the wire has carried this since OPBridge S153 and the
 *  PWA model dropped it, so no surface could offer per-zone control. */
export interface FixtureSubfixture {
  index: number;
  name: string;
  /** Capability NAMES only - the wire sends names here, not the full
   *  `FixtureCapability` records that the mode carries. */
  capabilities: FixtureCapabilityName[];
}

/** A mode within a fixture profile. Fixtures can have multiple modes
 *  (e.g. 3-channel vs 7-channel). The instance picks one mode. */
export interface FixtureProfileMode {
  name: string;
  channelCount: number;
  capabilities: FixtureCapability[];
  /** Built-in FX macro slot names declared by this mode, e.g. the Neewer
   *  PL60C's "FX" mode declaring `police` / `lightning` / `fire` / 14 more.
   *  Empty for a mode with no FX personality. These are the fixture's OWN
   *  macros, written as channel values - distinct from OPBridge's software
   *  effects (`GET /dmx/fx-catalog`), which it renders itself. A surface
   *  offering effects must keep the two groups visually separate, because
   *  only the software ones can be clock-synced. */
  effects?: string[];
  /** Addressable zones, in profile order. Empty for single-block fixtures. */
  subfixtures?: FixtureSubfixture[];
}

/** The broad KIND of light, from OPBridge's pinned seven-name vocabulary
 *  (`opbridge_fixtures::ARCHETYPE_VOCABULARY`). `null`/absent for a profile
 *  that genuinely fits none - group those under "Other" rather than forcing
 *  a bucket.
 *
 *  Exists so consumers stop regexing the `profileId` to guess: OPBridge
 *  measured that guess wrong on 4 of the 7 profiles added since OPStage
 *  wrote it (PROTOCOL.md § "Fixture archetypes"). It is the right grouping
 *  for any make/model picker, because it is how the operator thinks about
 *  a light before they think about its brand. */
export type FixtureArchetype =
  | 'wash' | 'strobe' | 'bar' | 'par' | 'mover' | 'beam' | 'laser';

/** Vendored profile from OPBridge's crates/opbridge-fixtures/manual/**.
 *  The PWA caches the modes it needs alongside each instance for offline
 *  rendering; full profiles are OPBridge's territory. */
export interface FixtureProfile {
  id: string;
  name: string;
  manufacturer: string;
  model: string;
  modes: FixtureProfileMode[];
  /** Absent on the built-in seed and on any profile OPBridge declines to
   *  bucket. Never inferred from `id` - see `FixtureArchetype`. */
  archetype?: FixtureArchetype | null;
}

/** An instance of a fixture placed in the user's rig. Mirrors OPBridge's
 *  `FixtureInstance` (PROTOCOL.md v1.1 § "Wire shapes"). */
export interface FixtureInstance {
  /** Stable id. ULID or slug. */
  id: string;
  /** User label ("Left PAR", "Front Wash", "Drums Strobe"). */
  name: string;
  /** Reference into the profile registry (`chauvet-dj/slimpar-56` etc.). */
  profileId: string;
  /** Which mode of the profile. */
  mode: string;
  /** DMX universe (default 1). Small rigs use only universe 1. */
  universe: number;
  /** 1-based DMX start address (1..512). Instance's channels are
   *  [dmxAddress .. dmxAddress + channelCount - 1]. */
  dmxAddress: number;
  /** Optional stage-position hint (m). Not required for timeline editing. */
  positionHint?: { x: number; y: number; z: number };
  /** Optional notes. */
  notes?: string;

  // ─── Fields OPBridge owns that we must carry, not consume ──────────────
  // Added 2026-09-17. `PUT /fixtures/{id}` takes a WHOLE FixtureInstance and
  // OPBridge's update path deliberately never re-stamps `outputNodeId` ("the
  // PUT path never auto-stamps - the body owns it"). A PWA that models eight
  // of OPBridge's eleven fields therefore STRIPS the other three on its first
  // edit: the mesh owner, the venue-environment stamp and the operator's
  // groups. Same class as the timeline envelope (CP-139), in the write
  // direction. Modelled now, while there is still no caller to get it wrong.

  /** Which mesh node physically drives this fixture (DEC-471). A fixture
   *  owned by another node is dropped rather than relayed, permanently and
   *  by design - so losing this silently re-homes the light. Never authored
   *  here; OPBridge stamps it at patch time. Carry it verbatim. */
  outputNodeId?: string | null;
  /** Operator-authored group tags ("wash", "front"). The most novice-legible
   *  target a lighting control can have - "All wash lights" beats picking
   *  three fixtures by name - so this is READ and offered as a target, even
   *  while authoring groups stays OPBridge's. */
  groups?: string[];
  /** Venue-context stamp (DEC-470). OPBridge stamps the active environment
   *  at create time. Carry it verbatim; the environment arc is benched
   *  OPButtons-side (BENCHED.md, per-environment arc). */
  environmentId?: string | null;
  /** Non-DMX transport (Hue, WLED, ...). A fixture with a transport may
   *  legitimately have `dmxAddress: 0` - OPBridge's own sentinel for "this
   *  fixture has no DMX address", chosen so an older daemon parses it and
   *  refuses to drive it rather than failing to parse and emptying the whole
   *  rig. Opaque here. */
  transport?: unknown;
  /** Anything OPBridge sends that this version does not model. Kept so an
   *  edit round-trips a field added after this build shipped, instead of
   *  deleting it. Never read for behaviour - if a surface needs a value in
   *  here, model it above instead. */
  passthrough?: Record<string, unknown>;

  /** Cached profile modes so the PWA can render sub-lanes without a live
   *  OPBridge round-trip. Populated when the instance is created (from
   *  the seeded profile catalog for local seeds; from OPBridge when the
   *  PWA fetches the profile). Runtime ignores.
   *
   *  Now a FALLBACK, not the primary: `fixtures/profileSync.ts` holds the
   *  live `GET /dmx/profiles` catalogue and is consulted first. */
  cachedProfile?: FixtureProfile;
}

/** Storage envelope. */
export const FIXTURES_SCHEMA_VERSION = 'opbuttons-fixtures/v1' as const;
export interface FixtureLibrary {
  schemaVersion: typeof FIXTURES_SCHEMA_VERSION;
  fixtures: FixtureInstance[];
}

/**
 * WHICH LIGHTS a lighting control acts on.
 *
 * This is the whole novice-facing vocabulary of the lighting module, and it
 * is deliberately the only vocabulary. DEC-014 calls capability-binding "the
 * architectural moat": a control names a capability on a fixture and OPBridge
 * resolves the channels. This type is the operator-facing half of that lock -
 * there is no variant here that carries a universe or an address, so no UI
 * built on it can ask for one, and the words "DMX address" never reach an
 * operator who did not go looking for them.
 *
 * Four variants, in the order an operator reaches for them:
 *
 *  - `all`      every fixture in the rig. The "I want the room red" target,
 *               and the one a first-time user will pick.
 *  - `group`    an operator-authored tag ("wash", "front"). One tap for a
 *               thing that otherwise costs several, and the reason group
 *               AUTHORING is in scope (operator, 2026-09-17) rather than
 *               read-only: a target vocabulary you cannot extend goes stale
 *               the first time you add a light.
 *  - `fixture`  one named light.
 *  - `zone`     one Block of a multi-zone fixture. NOT an advanced extra:
 *               the COLORband PiX, 4PLAY and Cubix 2.0 declare every
 *               capability on their zones, so on this operator's real rig a
 *               zoned fixture is a third of it. Addressing the fixture
 *               without a zone FANS the write to every zone, which is a
 *               reasonable default and the wrong thing whenever the operator
 *               meant Block 3.
 *
 * `all` and `group` are resolved to concrete fixture ids CLIENT-SIDE, because
 * OPBridge's drive routes are per-fixture. That is a deliberate fan-out of N
 * requests rather than a new endpoint ask: N is the size of a rig (five here),
 * the routes are rate-limited at 50 r/s, and a group is an OPButtons-facing
 * grouping that OPBridge has no reason to learn.
 */
export type DmxTarget =
  | { kind: 'all' }
  | { kind: 'group'; group: string }
  | { kind: 'fixture'; fixtureId: string }
  | { kind: 'zone'; fixtureId: string; subfixture: number };

/** The target a freshly-placed lighting control starts on. Whole rig, because
 *  it is the one target that is guaranteed to mean something before the
 *  operator has picked anything - a control that does nothing until it is
 *  configured is the "loud dead state" this product otherwise reserves for
 *  genuinely broken things. */
export const DEFAULT_DMX_TARGET: DmxTarget = { kind: 'all' };

// =====================================================================
// A sequencer / macro lane that drives a light (2026-09-28)
// =====================================================================

/**
 * The DMX half of a sequencer or macro lane target: a NAMED capability on
 * one patched fixture, optionally one zone - never a channel number
 * (DEC-103). OPBridge's `DmxLaneTarget`, tagged `type: 'dmx'` on the wire
 * (their `MacroLaneTarget` / `SequencerLaneTarget`); a MIDI lane carries no
 * `type` at all, which OPBridge reads as its legacy MIDI shape.
 *
 * The value it gets is the lane's 0..127 output normalised to 0..1 by
 * OPBridge: a sequencer step's velocity, a macro's shape. A DMX lane has no
 * note-off; its "release" is simply the next value.
 *
 * `subfixtureIndex` absent = the whole fixture, which for a zoned fixture
 * FANS to every zone (`fixtureZones`).
 */
export interface LaneDmxTarget {
  type: 'dmx';
  fixtureId: string;
  subfixtureIndex?: number;
  capability: FixtureCapabilityName;
}

export function isDmxLaneTarget(t: unknown): t is LaneDmxTarget {
  return typeof t === 'object' && t !== null && (t as { type?: unknown }).type === 'dmx';
}
